import classNames from "classnames";
import { InputHTMLAttributes, ReactNode, forwardRef } from "react";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";

import Label from "../../atomic/Label";
import { ErrorTextBox } from "../../atomic/ErrorBox";
import { whiteLabelStyling } from "../../../shared/utils/constants";

interface IProps
  extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  styles?: {
    container?: string;
    label?: string;
    input?: string;
    textarea?: string;
  };
  label?: ReactNode;
  register?: any;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
  errorText?: string;
  requiredIcon?: boolean;
  pattern?: string;
  autoComplete?: AutoFill | undefined;
  style?: any;
}

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, IProps>(
  (
    {
      styles = {},
      label,
      name = "",
      type,
      autoComplete = "off",
      requiredIcon = false,
      onChange = undefined,
      value,
      placeholder,
      register,
      error,
      disabled = false,
      errorText = "",
      multiple = false,
      pattern = undefined,
      style,
      ...rest
    },
    ref = null
  ) => {
    const { whiteLabelStyle } = whiteLabelStyling();
    return (
      <div
        className={classNames(
          "",
          styles.container ? styles.container : "sm:col-span-4"
        )}
      >
        {label && (
          <Label
            name={name}
            label={label}
            style={styles.label}
            requiredIcon={requiredIcon}
          />
        )}
        {multiple ? (
          <div>
            <textarea
              ref={ref}
              id={name || undefined}
              type={type}
              value={value}
              disabled={disabled}
              autoComplete={autoComplete}
              className={classNames(
                "block w-full rounded-md border-gray-200 sm:text-sm lg:text-xs px-4 py-2",
                whiteLabelStyle?.theme?.hover?.focus ||
                  "focus:border-indigo-500",
                whiteLabelStyle?.theme?.hover?.ring || "focus:ring-indigo-500",
                disabled && " bg-[#F5F7F8] cursor-not-allowed",
                styles.textarea || ""
              )}
              style={{ ...style, backgroundColor: disabled ? "#F5F7F8" : "" }}
              placeholder={placeholder}
              onChange={(e) => {
                e.stopPropagation();
                onChange?.(e);
              }}
              rows={5}
              spellCheck={false}
              {...register}
              {...rest}
            />

            {errorText ? <ErrorTextBox text={errorText}></ErrorTextBox> : null}
          </div>
        ) : (
          <div>
            <input
              ref={ref}
              id={name || undefined}
              type={type}
              value={value}
              disabled={disabled}
              autoComplete={autoComplete}
              className={classNames(
                "block w-full rounded-md border-gray-200 sm:text-sm lg:text-xs px-4 py-2 placeholder:text-[#CECFCF]",
                whiteLabelStyle?.theme?.hover?.focus ||
                  "focus:border-indigo-500",
                whiteLabelStyle?.theme?.hover?.ring || "focus:ring-indigo-500",
                disabled ? "bg-[#F5F7F8] cursor-not-allowed" : "",
                styles.input || ""
              )}
              style={{ ...style, backgroundColor: disabled ? "#F5F7F8" : "" }}
              placeholder={placeholder}
              onChange={(e) => {
                e.stopPropagation();
                onChange?.(e);
              }}
              spellCheck={false}
              {...register}
              {...rest}
            />
            {errorText ? <ErrorTextBox text={errorText}></ErrorTextBox> : null}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
